import { FormGroup } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IOrganization } from '@frontend/components/interfaces';
import { convertOrganizationTypeToOrganizationTypeWithLinkedProfessions } from '@common/utils/convertOrganizationTypeToOrganizationTypeWithLinkedProfessions';
import { getOneOrganization } from '@frontend/services/api/organizationsApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { Profession } from '../../../../../common/enums/profession';
import type { GeneralSettingsProps } from './generalSettingsProps';
import { SelectProfession } from '../../../elements/SelectProfession';
import { GeneralSettingsReadOnlyFormGroup } from './GeneralSettingsReadOnlyFormGroup';

export function GeneralSettingsProfession(props: GeneralSettingsProps) {
    const abortSignal = useAbortSignal();
    const [selectedProfession, setSelectedProfession] = useState<Profession | null>(null);
    const [selectedProfessionLabel, setSelectedProfessionLabel] = useState<string | null>(null);
    const [organization, setOrganization] = useState<IOrganization | undefined>();

    const translate = getSelectedLanguage();

    if (!props.employment) {
        return null;
    }

    useEffect(() => {
        loadOrganization(props.employment.organizationId);
    }, [props.employment.organizationId]);

    useEffect(() => {
        if (props.employment.profession) {
            initializeEmploymentProfession(props.employment.profession);
        }
    }, [props.employment.profession]);

    if (!organization) {
        return null;
    }

    if (!props.isEditing) {
        return (
            <GeneralSettingsReadOnlyFormGroup
                label={translate.common.profession}
                value={selectedProfessionLabel}
            />
        );
    }

    return (
        <FormGroup
            hasErrors={props.errors.has('profession')}
            isRequired={true}
            label={translate.common.profession}
        >
            <SelectProfession
                error={props.errors.get('profession')}
                onChange={handleChange}
                organizationType={
                    organization.organizationType
                        ? convertOrganizationTypeToOrganizationTypeWithLinkedProfessions(organization.organizationType)
                        : null
                }
                placeholder={translate.common.clickAndChooseFromList}
                profession={selectedProfession}
            />
        </FormGroup>
    );

    function handleChange(profession: Profession | null) {
        initializeEmploymentProfession(profession);

        props.onChange('profession', profession);
        props.onValidateOne('profession', profession);
    }

    function initializeEmploymentProfession(profession: Profession | null) {
        const professionLabel = profession ? translate.professionEnum[profession] : null;

        setSelectedProfession(profession);
        setSelectedProfessionLabel(professionLabel);
    }

    async function loadOrganization(organizationId: string) {
        const result = await getOneOrganization(organizationId, abortSignal);
        setOrganization(result);
    }
}
